import mpx from '@mpxjs/core'
import { GlobalEnv } from '../global_env'
import { request as webxRequest, xenv, postEncrypt } from '@didi/webx-js'

/**
 * @typedef RequestOptions
 * @property {boolean} [needSign = true] - 是否需要签名
 * @property {Object} [urlQueries] - URL Query 参数，只有 post 用。methods 是 post 的时候，params 会当成 data. 如果需要 url 参数，这里指定
 * @property {boolean} [useFormData = false] - 是否使用 FormData
 * @property {boolean} [ignoreError = false] - 是否忽略错误。忽略错误会 resolve undefined, 不会 reject
 */

const urlRemoveWsgsig = url => {
  const [path, rest] = url.split('?')
  if (rest) {
    const [queryStr, hash] = rest.split('#')
    const queries = queryStr
      .split('&')
      .map(item => item.split('='))
      .reduce(
        (prev, [key, value]) =>
          Object.assign(prev, {
            [key]: value,
          }),
        {}
      )
    delete queries.wsgsig
    const newQuery = Object.entries(queries)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
    return `${path}?${newQuery}${hash ? '#' + hash : ''}`
  } else {
    return url
  }
}

let GlobalRetriedCounter = 0

const shouldRetry = error => {
  if (error.requestConfig.method.toLowerCase() !== 'get') return false
  if (error.errMsg) {
    if (error.errMsg.indexOf('request:fail Error: timeout of') !== -1) {
      const url = error.requestConfig.url
      if (
        url.indexOf('/quartz/goods/homepage') > -1 ||
        url.indexOf('/quartz/goods/lists') > -1 ||
        url.indexOf('/quartz/order/lists') > -1
      ) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  } else {
    return false
  }
}

mpx.xfetch.interceptors.response.use(
  res => res,
  error => {
    if (shouldRetry(error)) {
      if (
        GlobalRetriedCounter < 20 &&
        (!error.requestConfig.retryTime || error.requestConfig.retryTime < 2)
      ) {
        error.requestConfig.retryTime = error.requestConfig.retryTime
          ? error.requestConfig.retryTime + 1
          : 1
        error.requestConfig.url = urlRemoveWsgsig(error.requestConfig.url)
        GlobalRetriedCounter += 1
        return mpx.xfetch.fetch(error.requestConfig)
        // return webxRequest.instance._request.fetch(error.requestConfig)
      } else {
        return Promise.reject(error)
      }
    } else {
      return Promise.reject(error)
    }
  }
)

/**
 * 带错误处理的请求封装
 * @param {('GET' | 'POST')} - HTTP 方法
 * @param {string} url - URL
 * @param {*} [params] - 参数
 * @param {RequestOptions} [options] - 选项
 */
export const request = async (method, url, params, options = {}) => {
  try {
    const useFormData =
      options && options.useFormData !== undefined
        ? !!options.useFormData
        : false
    const _params = {
      lang: GlobalEnv.lang,
      // 'hypassenger' 后端有针对版本做处理。改动回归量过大，因此本次先不加
      ...(__mpx_mode__ === 'web' && ['passenger'].includes(xenv())
        ? {
            app_version: GlobalEnv.appVersion,
          }
        : {}),
      ...params,
    }
    const commonConfig = {
      needSign:
        options && options.needSign !== undefined ? !!options.needSign : true,
      emulateJSON: useFormData,
      timeout: 8200,
    }
    if (options.header) {
      commonConfig.header = options.header
    }
    const { errno, data, errmsg, trace_id } = options?.postEncrypt
      ? await postEncrypt(url, _params, commonConfig)
      : await webxRequest(method, url, {
          ...(method === 'POST'
            ? {
                data: _params,
                params: options && options.urlQueries ? options.urlQueries : {},
              }
            : {
                params: _params,
              }),
          ...commonConfig,
        })
    if (errno === 0) {
      return Promise.resolve(data)
    } else if (errno) {
      // 为什么要再次判断 errno 存在：因为 request 报错被下面 catch 之后，这里的流程还是会走的，errno 是 undefined. 会造成两次报错。
      const err = new Error('Response Error.')
      Object.assign(err, {
        extra: {
          errno,
          errmsg,
          traceId: trace_id || '',
        },
      })
      throw err
    }
  } catch (error) {
    if (options && options.ignoreError) {
      return Promise.resolve()
    } else {
      return Promise.reject(error)
    }
  }
}

/**
 * 带错误处理的 GET 请求封装
 * @param {string} url - URL
 * @param {*} [params] - 参数
 * @param {RequestOptions} [options] - 选项
 */
request.get = (url, params, options) => request('GET', url, params, options)

/**
 * 带错误处理的 POST 请求封装
 * @param {string} url - URL
 * @param {*} [params] - 参数
 * @param {RequestOptions} [options] - 选项
 */
request.post = (url, params, options) => request('POST', url, params, options)

/**
 * 带错误处理的 加密POST 请求封装
 * @param {string} url - URL
 * @param {*} [params] - 参数
 * @param {RequestOptions} [options] - 选项
 */
request.postEncrypt = (url, params, options) =>
  request('POST', url, params, {
    ...options,
    postEncrypt: true,
  })
